
import { Translations } from "../logic/Translations";
import { DatelyObject } from "./DatelyObject";
import { Dates } from "./Dates";


import dayjs from "dayjs";
import CustomParseFormat from "dayjs/plugin/customParseFormat";
import AdvancedFormat from "dayjs/plugin/advancedFormat";
import Duration from "dayjs/plugin/duration";
import IsoWeek from "dayjs/plugin/isoWeek";
import IsSameOrBefore from "dayjs/plugin/isSameOrBefore";
import IsSameOrAfter from "dayjs/plugin/isSameOrAfter";
import UTC from "dayjs/plugin/utc";
import QuarterOfYear from "dayjs/plugin/quarterOfYear";

dayjs.extend(CustomParseFormat);
dayjs.extend(AdvancedFormat);
dayjs.extend(Duration);
dayjs.extend(IsoWeek);
dayjs.extend(IsSameOrBefore);
dayjs.extend(IsSameOrAfter);
dayjs.extend(UTC);
dayjs.extend(QuarterOfYear);


/**
 * Date/Time parsing, formatting and other operations within the Salaxy framework.
 * We use ISO 8601 as our Date/Time format even inside JavaScript objects.
 * This is because of the following reasons:
 *
 * 1. Generally, we do not want to use JavaScript Date because it presents so many problems.
 * 2. We store dates in JSON as ISO 8601 date strings - this is universally the best practice
 * 3. ISO 8601 provides a way to present a date without time and most importantly timezone information
 * 4. ISO 8601 date also works best for most UI components (because of similar reasons as above).
 *
 * IMPORTANT: Use Date/Time only where TIME and/or timezone is relevant.
 * Otherwise use Date (Dates library) that does not have time or timezone.
 *
 * Implementation uses Day.js
 */
export class DateTimes {

  /**
   * Adds (or substracts) hours, minutes or seconds to a dately object.
   * @param datelyObject Base timestamp for the addition.
   * @param amount Number of units to add
   * @param unit Unit for the addition: hour,minute,second
   * @returns DatelyObject
   */
  public static add(datelyObject: DatelyObject | undefined, amount: number, unit: "hour" | "minute" | "second" | undefined ): DatelyObject | undefined {
    const newDayJs = Dates.getDayJs(datelyObject);
    if (!newDayJs || !amount || !unit) {
      return undefined;

    }
    switch (unit) {
      case "hour":
        return newDayJs.add(amount, "hour");
      case "minute":
        return newDayJs.add(amount, "minute");
      case "second":
        return newDayJs.add(amount, "second");
      default:
        throw new Error(`Unit "${unit}" is not supported.`);
    }
  }

  /**
   * Gets date in HH:mm formatted string
   * @param date Object that should be formatted
   * @returns HH:mm formatted string
   */
    public static asTime(date: Date): string | undefined {
      const dayJs = Dates.getDayJs(date);
      return dayJs ? dayJs.format("HH:mm") : undefined;
    }

    /**
     * Gets hours and minutes from date and convert them to minutes integer
     * @param date Date used to get hours and minutes
     * @returns Minutes as integer
     */
      public static asMinutes(date: Date): number | undefined {
        const dayJs = Dates.getDayJs(date);
        return dayJs ? dayJs.hour() * 60 + dayJs.minute() : undefined;
      }

      /**
       * Convert time to ISO datetime string.
       * @param time Timestamp, HH:mm
       * @returns Iso datetime
       */
      public static fromTime(time: string): Date {
        if (!time) {
          return new Date();
        }
        return new Date(`${Dates.getToday()}T${time}:00`);
      }

      /**
       * Converts minutes to JSDate object. Adds minutes to start of current day.
       * @param minutes Used minutes to create JSDate
       * @returns JSDate
       */
      public static fromMinutes(minutes: number): Date | undefined {
        return Dates.asJSDate(Dates.getDayJs("today").minute(minutes));
      }






  /**
   * Formats a datetime ISO string (or other dately object)
   * as time with texts "Today", "Yesterday", "D.M. HH:mm" for this year
   * and "D.M.YYYY" for other years (if necessary, add option for adding time to previous years).
   *
   * @param time Dately object to format
   */
  public static format(time: DatelyObject): string {
    const djs = Dates.getDayJs(time);
    if (!djs) {
      return "-";
    }
    if (Dates.getDayJs("today").isSame(djs, "date")) {
      return Translations.getWithDefault("SALAXY.UI_Terms.timeToday", "Today") + " " + djs.format("HH:mm");
    }
    if (Dates.getDayJs("yesterday").isSame(djs, "date")) {
      return Translations.getWithDefault("SALAXY.UI_Terms.timeYesterday", "Yesterday") + " " + djs.format("HH:mm");
    }
    if (Dates.getDayJs("today").isSame(djs, "y")) {
      return djs.format("D.M. HH:mm");
    }
    return djs.format("D.M.YYYY");
  }

  /**
   * Formats a date/time range to an end-user friendly duration text. Note that durations are not context bound.
   * So translation of longer durations depend on context (years, months)
   * @param start Start of the range as ISO string.
   * @param end End of the range as ISO string.
   * @param nullValue Value if start or end is missing or cannot be parsed.
   */
  public static formatRangeToDuration(start: string, end: string, nullValue = "-"): string {
    if (!start || !end) {
      return nullValue;
    }
    if (end < start) {
      return this.formatDurationFromDayJs(Dates.getDuration(end, start), nullValue) + " ennen";
    }
    return this.formatDurationFromDayJs(Dates.getDuration(start, end), nullValue);
  }

  /**
   * Formats a date/time range to an end-user friendly duration text.
   * @param duration Duration as ISO duration string (e.g. P3Y6M4DT12H30M5S)
   * @param nullValue Value if duration is missing or cannot be parsed.
   */
  public static formatDuration(duration: string, nullValue = "-"): string {
    if (!duration) {
      return nullValue;
    }
    let postfix = "";
    if (duration.startsWith("-")) {
      postfix = " ennen";
      duration = duration.substr(1);
    }
    return this.formatDurationFromDayJs(dayjs.duration(duration), nullValue) + postfix;
  }

  private static formatDurationFromDayJs(duration: any, nullValue: string): string {
    if(!duration){
      return nullValue;
    }
    if (!dayjs.isDuration(duration)) {
      return nullValue;
    }
    let result = "";
    if (duration.years() !== 0)
    {
      result += duration.years() + " " +Translations.getWithDefault("SALAXY.UI_Terms.years", "vuotta")+ " ";
    }
    if (duration.months() !== 0)
    {
      result += duration.months() + " " +Translations.getWithDefault("SALAXY.UI_Terms.months", "kk")+ " ";
    }
    if (duration.days() !== 0)
    {
      result += duration.days() + " " +Translations.getWithDefault("SALAXY.UI_Terms.days", "pv")+ " ";
    }
    if (duration.hours() !== 0)
    {
      result += duration.hours() + " " +Translations.getWithDefault("SALAXY.UI_Terms.hours", "h")+ " ";
    }
    if (duration.minutes() !== 0)
    {
      result += duration.minutes() + " " +Translations.getWithDefault("SALAXY.UI_Terms.minutes", "min")+ " ";
    }
    return result.trim();
  }

  /**
   * Gets the hour as 0-based number: 0-23.
   *
   * @param datelyObject - Object that represents datetime: A string, JS Date object, DayJs etc.
   *
   * @returns hour as 0-23 or undefined if the object is not a dately object.
   */
  public static getHour(datelyObject: DatelyObject | undefined): number | undefined {
    const dayJs = Dates.getDayJs(datelyObject);
    return dayJs ? dayJs.hour() : undefined;
  }

  /**
   * Gets the minute as 0-based number: 0-59.
   *
   * @param datelyObject - Object that represents datetime: A string, JS Date object, DayJs etc.
   *
   * @returns hour as 0-59 or undefined if the object is not a dately object.
   */
  public static getMinute(datelyObject: DatelyObject): number | undefined {
    const dayJs = Dates.getDayJs(datelyObject);
    return dayJs ? dayJs.minute() : undefined;
  }

  /**
   * Gets the second as 0-based number: 0-59.
   *
   * @param datelyObject - Object that represents datetime: A string, JS Date object, DayJs etc.
   *
   * @returns second as 0-59 or undefined if the object is not a dately object.
   */
  public static getSecond(datelyObject: DatelyObject): number | undefined {
    const dayJs = Dates.getDayJs(datelyObject);
    return dayJs ? dayJs.second() : undefined;
  }

  /**
   * WARNING: Don't use this method. This may be refactored away.
   * Wraps DayJs UTC offset (minutes).
   * See: https://day.js.org/docs/en/manipulate/utc-offset
   * @param timestamp any DayJs compatible DatelyObject
   * @returns number UTC offset in minutes
   */
    public static getUTCOffset(timestamp: DatelyObject | undefined) : number | undefined {
      const d = Dates.getDayJs(timestamp);
      return d ? d.utcOffset() : undefined;
    }


}
